/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Player } from 'video.js';

import Imgix from 'js/components/Imgix';

import { SvgLoaderSignal } from '@coursera/coursera-ui/svg';

import type TrackList from 'bundles/interactive-transcript/models/TrackList';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type OnDemandVideosV1 from 'bundles/naptimejs/resources/onDemandVideos.v1';
import TrackedButton from 'bundles/page/components/TrackedButton';
import inServerContext from 'bundles/ssr/util/inServerContext';
import VideoJSPlayerWrapper from 'bundles/video-logged-out-page/components/video-player/VideoJSPlayerWrapper';
import VideoPlayerModel from 'bundles/video-logged-out-page/models/VideoPlayerModel';

import _t from 'i18n!nls/articles';

type BigPlayButtonProps = { onPlay: () => void; trackingData: Record<string, string> };
type VideoPlaceholderProps = {
  src: string;
  onPlayVideo: () => void;
  trackingData: Record<string, string>;
};

type Props = {
  onDemandVideos: OnDemandVideosV1;
  articleSlug: string;
  courseSlug: string;
  itemId: string;
  videoIsReady: boolean;
  showVideoOverlay: boolean;
  getVideoPlayerObject: (videojs: Player) => void;
  onPlayVideo: () => void;
  videoPlayer: Player | null;
  tracks: TrackList | undefined;
  showPlaceholder?: boolean;
};

const styles = {
  articleVLPVideoPlayer: css`
    max-width: 780px;
    position: relative;
    padding-bottom: 5px;
    margin: 0 auto;
    margin-bottom: 24px;

    .vjs-subtitles-button {
      .vjs-menu {
        li {
          text-transform: capitalize;
        }
      }
    }

    .rc-VideoPlayerContainer__video-wrapper {
      position: relative;
      padding-top: 56.25%;
      overflow: hidden;
      background: var(--cds-color-neutral-primary);

      .video-overlay {
        height: 100%;
        width: 100%;
        position: absolute;
        top: 0;
        left: 0;
        background: radial-gradient(circle, rgb(8 39 78 / 70%) 0%, var(--cds-color-blue-950) 100%);
      }

      .video-js {
        position: absolute;
      }

      .vjs-big-play-centered {
        .vjs-big-play-button {
          border-radius: 50%;
          height: 3em;
          margin: 0;
          top: calc(50% - 1.5em);
          left: calc(50% - 1.5em);

          span {
            margin-left: 0.2em;
            margin-top: 0.2em;
          }
        }

        .video-placeholder {
          width: 100%;
        }
      }

      .rc-VideoJSPlayerWrapper__video-wrapper {
        .rc-VideoJSPlayer {
          .vjs-big-play-button {
            display: none;
          }
        }
      }

      .loading-wrapper {
        position: absolute;
        top: calc(50% - 32px);
        left: calc(50% - 32px);
      }
    }
  `,
};

const BigPlayButton = ({ onPlay, trackingData }: BigPlayButtonProps) => (
  <div className="video-overlay video-js vjs-big-play-centered">
    <TrackedButton
      className="vjs-big-play-button"
      type="button"
      aria-live="polite"
      onClick={onPlay}
      trackingName="click_play_video_button_article_page"
      data={trackingData}
    >
      <span className="cif-lg cif-fw cif-play" />
      <span className="vjs-control-text">{_t('Play Video')}</span>
    </TrackedButton>
  </div>
);

const LoaderSignalWithOverlay = () => (
  <div>
    <div className="video-overlay" />
    <div className="loading-wrapper">
      <SvgLoaderSignal size={64} />
    </div>
  </div>
);

const VideoPlaceholder = ({ src, onPlayVideo, trackingData }: VideoPlaceholderProps) => (
  <div className="video-overlay video-js vjs-big-play-centered">
    <Imgix alt={_t('Video placeholder')} src={src} maxWidth="100%" className="video-placeholder" />
    {!inServerContext ? (
      <div>
        <BigPlayButton onPlay={onPlayVideo} trackingData={trackingData} />
      </div>
    ) : (
      <LoaderSignalWithOverlay />
    )}
  </div>
);

class VLPVideoPlayer extends React.Component<Props> {
  componentWillReceiveProps(nextProps: Props) {
    const { videoIsReady } = this.props;

    if (!videoIsReady && nextProps.videoIsReady && nextProps.videoPlayer) {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'tracks' does not exist on type 'TrackLis... Remove this comment to see the full error message
      nextProps.tracks?.tracks?.forEach(({ label, languageCode, url }) => {
        nextProps.videoPlayer?.addRemoteTextTrack(
          {
            kind: 'subtitles',
            label,
            language: languageCode,
            src: url,
          },
          true
        );
      });
      nextProps.videoPlayer.play();
    }
  }

  render() {
    const {
      videoIsReady,
      getVideoPlayerObject,
      onDemandVideos,
      onPlayVideo,
      showPlaceholder,
      courseSlug,
      articleSlug,
      itemId,
    } = this.props;
    const imageResolution = Object.keys(onDemandVideos?.sources?.byResolution).find(
      (resolution) => resolution.toLowerCase() === '360p'
    );
    const screenshot = onDemandVideos?.sources?.byResolution?.[`${imageResolution}`]?.previewImageUrl;

    const videoModel = new VideoPlayerModel({ onDemandVideos });
    const trackingData = {
      courseSlug,
      itemId,
      articleSlug,
    };
    return (
      <div css={styles.articleVLPVideoPlayer}>
        <div className="rc-VideoPlayerContainer__video-wrapper">
          {showPlaceholder ? (
            <VideoPlaceholder onPlayVideo={onPlayVideo} src={screenshot} trackingData={trackingData} />
          ) : (
            <React.Fragment>
              <VideoJSPlayerWrapper videoModel={videoModel} getPlayer={getVideoPlayerObject} />
              {!videoIsReady && (
                <div className="loading-wrapper">
                  <SvgLoaderSignal size={64} />
                </div>
              )}
            </React.Fragment>
          )}
        </div>
      </div>
    );
  }
}
export default VLPVideoPlayer;
